import { useEffect, useMemo, useState } from 'react';
import { getExplanationList } from '../../../api/explanationService';
import { getSidoList } from '../../../api/regionService';

// 페이지 로드 시점의 "오늘" 기준 연도 - 기본으로 보여줄 달(month state 초기값)과 짝을 맞춰서 쓴다.
const YEAR = new Date().getFullYear();
const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 설명회 일정 "데이터 공급원(source of truth)" — 렌더링과 분리된 이음새(seam).
 * ─ 데이터 계약(contract) ─ 아래 형태를 반드시 지킬 것:
 *     { "<연도>-<월>": [ { d, mode, t, time, place, tel }, ... ], ... }
 *       d    : 날짜(숫자 1~31)
 *       mode : 'off' = 대면 / 'on' = 비대면(온라인)
 *       t    : 일정 제목(문자열) — REGION_CD를 CODE_MST(CD_GROUP=REGION, CD_TYPE=SIDO)의
 *              CD_NM(예: "부산광역시")으로 치환한 값. 매칭되는 코드가 없으면 원본 코드를 그대로 보여준다.
 *       time : 시간(문자열, 생략 가능)
 *       place: 장소(문자열, 생략 가능)
 *       tel  : 문의 연락처(문자열, 비어있으면 신청서 팝업에서 미노출)
 *
 * WWW 백엔드 GET /api/explanations(BRD_EXPLANATION_NEW 조회)를 호출해 연간 일정을 한 번에
 * 받아온 뒤, 위 계약 형태로 그룹핑한다. REGION_CD는 가맹문의/본부지원 신청서의 희망지역
 * 드롭다운과 동일한 CODE_MST(CD_GROUP=REGION) 시/도 코드값이라, GET /api/regions/sido로
 * 조회한 목록에서 같은 cd를 찾아 cdNm(지역명)으로 바꿔서 보여준다.
 *
 * TODO: BRD_EXPLANATION_NEW에는 대면/비대면을 구분하는 컬럼이 없다. 컬럼이 추가되기 전까지는
 * REGION/PLACE 텍스트에 "비대면"/"온라인"이 포함되어 있는지로 추정한다(부정확할 수 있음) —
 * 컬럼이 생기면 이 휴리스틱을 지우고 실제 값을 그대로 매핑해야 한다.
 */
async function loadSchedule() {
  let list = [];
  let sidoList = [];
  try {
    const [explanationRes, sidoRes] = await Promise.all([
      getExplanationList({
        heldStartDate: `${YEAR}0101`,
        heldEndDate: `${YEAR}1231`,
      }),
      getSidoList().catch(() => []), // 지역명 조회 실패해도 코드값으로라도 보여준다.
    ]);
    list = explanationRes?.data || [];
    sidoList = sidoRes || [];
  } catch (err) {
    // 목록을 못 받아와도 캘린더 자체는 빈 상태로 정상 렌더링되게 한다.
    console.error('사업설명회 일정을 불러오지 못했습니다.', err);
    return {};
  }

  const sidoNameByCd = new Map(sidoList.map((sido) => [sido.cd, sido.cdNm]));

  const grouped = {};
  list.forEach((item) => {
    const heldDate = item.heldDate;
    if (!heldDate || heldDate.length < 8) return;

    const year = heldDate.slice(0, 4);
    const month = parseInt(heldDate.slice(4, 6), 10);
    const day = parseInt(heldDate.slice(6, 8), 10);
    const key = `${year}-${month}`;

    const startTime = item.startTime || '';
    const time = startTime.length >= 4 ? `${startTime.slice(0, 2)}:${startTime.slice(2, 4)}` : undefined;

    const modeGuessText = `${item.region || ''} ${item.place || ''}`;
    const mode = /비대면|온라인/.test(modeGuessText) ? 'on' : 'off';

    const regionLabel = sidoNameByCd.get(item.regionCd) || item.regionCd;

    if (!grouped[key]) grouped[key] = [];
    grouped[key].push({
      d: day,
      mode,
      t: regionLabel,
      time,
      place: item.place,
      tel: item.tel,
      expIdx: item.expIdx,
    });
  });

  return grouped;
}

/**
 * [모듈 3] 설명회 일정 캘린더.
 * loadSchedule()에서 데이터를 불러와 월별 달력 격자/모바일 아젠다에 필요한 데이터를 계산한다.
 * 원본은 DOM을 직접 생성(createElement/innerHTML)했지만, 여기서는 동일한 계산 결과를
 * React 컴포넌트가 선언적으로 렌더링할 수 있도록 구조화된 데이터로 반환한다.
 * 오늘 날짜 기준 현재 달을 기본으로 열고(2026-09-16 수정 - 예전엔 8월로 하드코딩), 9~12월
 * (설명회 미개설 기간)에 일정이 없으면 showEmptyPanel이 true가 되어 안내 패널을 띄운다.
 */
export default function useSchedule() {
  const [scheduleData, setScheduleData] = useState(null);
  // 기본으로 열리는 달 - "오늘" 기준 현재 월 (예전엔 8월로 하드코딩되어 있었음)
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);

  useEffect(() => {
    let mounted = true;
    loadSchedule().then((data) => {
      if (mounted) setScheduleData(data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const events = useMemo(() => {
    if (!scheduleData) return [];
    return scheduleData[`${YEAR}-${month}`] || [];
  }, [scheduleData, month]);

  const showEmptyPanel = month >= 9 && events.length === 0;

  const grid = useMemo(() => {
    const firstWeekday = new Date(YEAR, month - 1, 1).getDay();
    const daysInMonth = new Date(YEAR, month, 0).getDate();
    const byDay = {};
    events.forEach((e) => {
      (byDay[e.d] = byDay[e.d] || []).push(e);
    });
    const totalCells = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;
    const today = new Date();
    const cells = [];
    for (let i = 0; i < totalCells; i++) {
      const col = i % 7;
      const dayNum = i - firstWeekday + 1;
      const isEmpty = dayNum < 1 || dayNum > daysInMonth;
      const isToday =
        !isEmpty && today.getFullYear() === YEAR && today.getMonth() + 1 === month && today.getDate() === dayNum;
      cells.push({
        key: i,
        isSun: col === 0,
        isSat: col === 6,
        isEmpty,
        isToday,
        dayNum: isEmpty ? null : dayNum,
        events: isEmpty ? [] : byDay[dayNum] || [],
      });
    }
    return cells;
  }, [events, month]);

  const agenda = useMemo(() => {
    const byDay = {};
    events.forEach((e) => {
      (byDay[e.d] = byDay[e.d] || []).push(e);
    });
    return Object.keys(byDay)
      .map(Number)
      .sort((a, b) => a - b)
      .map((d) => ({
        day: d,
        weekday: DAY_NAMES[new Date(YEAR, month - 1, d).getDay()],
        events: byDay[d],
      }));
  }, [events, month]);

  return {
    year: YEAR,
    month,
    setMonth,
    events,
    showEmptyPanel,
    grid,
    agenda,
    isLoaded: scheduleData !== null,
  };
}
